/* fm-checkout-guard.js: never let a customer reach Shopify's checkout.
 *
 * WHY THIS EXISTS (17 Oct 2026)
 *
 * This store cannot take money through Shopify. Shopify Payments is gone and
 * there is no Stripe, so /checkouts/... renders "This store can't accept
 * payments right now" with no card fields and no wallet. Lasso
 * (checkout.thefaithmade.com) is the only path that can charge a customer.
 *
 * Lasso works by rewriting the checkout button on load: it becomes
 * type="button", detached from the cart <form>, and Lasso navigates itself.
 * When Lasso's script fails to bind (it is intermittent, same family of
 * failures as the GemPages crash), the button is left as the theme shipped
 * it: type="submit" inside <form action="/cart">. Clicking it then submits
 * to Shopify and the customer lands on a checkout that cannot charge them.
 * That is not a degraded sale, it is a dead one.
 *
 * So: if Lasso has NOT taken the button over, we refuse to let the native
 * submit happen, and we drive Lasso ourselves via window.proceedToCheckout,
 * waiting for it (and re-requesting its script) if it has not arrived yet.
 *
 * This does not configure or modify Lasso. It only calls the entry point
 * Lasso already exposes, and re-requests the same script tag the theme
 * already loads.
 */
(function () {
  'use strict';

  var SEL = '#CartDrawer-Checkout, #checkout, [name="checkout"]';
  var LASSO_SRC = 'https://checkout.thefaithmade.com/shopify-scripts/lasso-script-v4.min.js';
  var MAX_TRIES = 24;        // ~12s at 500ms
  var RELOAD_KEY = '_fm_ckt_reloaded';

  function lassoReady() {
    return typeof window.proceedToCheckout === 'function';
  }

  // Lasso detaches the button from the cart form and makes it type=button.
  // If either is still true, Lasso has NOT taken it over.
  function isUnclaimed(btn) {
    return btn.getAttribute('type') === 'submit' || !!btn.closest('form');
  }

  // The theme gates checkout on a terms checkbox; respect it exactly as
  // theme checkoutLoad() does, so we never bypass a gate the theme set.
  function blockedByTerms() {
    var tnc = document.querySelector('#TNC-Checkbox-cart-drawer');
    return !!(tnc && tnc.dataset.checked !== 'true');
  }

  function requestLasso() {
    if (document.querySelector('script[data-fm-lasso-retry]')) return;
    var s = document.createElement('script');
    s.src = LASSO_SRC;
    s.async = true;
    s.setAttribute('data-fm-lasso-retry', '1');
    document.head.appendChild(s);
  }

  function tellCustomer(btn) {
    btn.classList.remove('loading');
    var msg = document.getElementById('fm-ckt-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.id = 'fm-ckt-msg';
      msg.setAttribute('role', 'alert');
      msg.textContent = 'We could not open secure checkout. Please refresh the page and try again.';
      if (btn.parentNode) btn.parentNode.insertBefore(msg, btn.nextSibling);
    }
  }

  function route(btn, tries) {
    tries = tries || 0;

    if (lassoReady()) {
      try { window.proceedToCheckout(); return; }
      catch (e) { /* fall through to retry/reload */ }
    }

    if (tries === 0) requestLasso();

    if (tries < MAX_TRIES) {
      setTimeout(function () { route(btn, tries + 1); }, 500);
      return;
    }

    // Lasso never arrived. One reload usually re-binds it, but only ever one:
    // a reload loop would be worse than the message.
    var tried;
    try { tried = sessionStorage.getItem(RELOAD_KEY); } catch (e) { tried = '1'; }
    if (!tried) {
      try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch (e) {}
      location.reload();
      return;
    }
    tellCustomer(btn);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest(SEL) : null;
    if (!btn) return;
    if (!isUnclaimed(btn)) return;        // Lasso owns it, stay out of the way
    if (blockedByTerms()) return;         // let the theme's own gate win

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    btn.classList.add('loading');
    route(btn, 0);
  }, true);

  // Enter-key submits and any other path into the cart form's checkout.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    if ((form.getAttribute('action') || '').indexOf('/cart') !== 0) return;

    var submitter = e.submitter;
    var isCheckout = submitter && submitter.getAttribute &&
      (submitter.getAttribute('name') === 'checkout' || submitter.id === 'checkout' ||
       submitter.id === 'CartDrawer-Checkout');
    if (!isCheckout) return;              // a normal cart update, leave it alone
    if (blockedByTerms()) return;

    e.preventDefault();
    e.stopPropagation();
    route(submitter, 0);
  }, true);

  // Clear the one-shot reload marker once a checkout page is actually reached.
  if (/checkout/i.test(location.hostname) || /^\/checkouts?\//.test(location.pathname)) {
    try { sessionStorage.removeItem(RELOAD_KEY); } catch (e) {}
  }
})();
